// Operations dashboard widget registry — small components that wrap IOSentra's
// existing health cards. All read the shared healthApi.summary() React Query
// (same queryKey → one fetch). Used by the editable WidgetGrid.
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  RadialBarChart, RadialBar, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import {
  Gauge, LineChart as LineChartIcon, PieChart as PieIcon, BarChart3, Layers, ListTree, Boxes, LayoutDashboard,
  Users, Briefcase, AlertTriangle, FileBarChart, Activity,
  Ticket, ShieldAlert, ArrowUpCircle, Truck, Server, Bell, Workflow, Video, ListChecks, Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { healthApi } from '@/lib/health-api';
import { inventoryApi, videoAssuranceApi, edgesApi } from '@/lib/inventory-api';
import { KpiStat } from '@/components/ui/primitives';

const OK = '#22c55e', WARN = '#eab308', CRIT = '#ef4444', PRIMARY = '#388eff';
const tooltip = { background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 };

// Shared query — every widget subscribes to the same key, so one fetch feeds all.
function useSummary() {
  return useQuery({ queryKey: ['health-summary'], queryFn: () => healthApi.summary(), refetchInterval: 10000 });
}

function KpiRowWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  // Real open-issue count from the incidents API — the SAME source as the bell
  // badge and the Issues page (audit: the KPI used to show critical+warnings
  // device faults under an "incidents" label, diverging from every other count).
  const { data: incidents } = useQuery({
    queryKey: ['health-incidents'], queryFn: () => healthApi.incidents(), refetchInterval: 10000, retry: false,
  });
  const openIssues = (incidents ?? []).filter((i) => i.status !== 'closed' && i.status !== 'resolved').length;
  // null score = no devices → show an em-dash + neutral tone (not a fake 0%).
  const score = s?.systemScore;
  // Recording cameras from the latest real snapshot (history7d carries it).
  const recCams = s?.history7d?.length ? s.history7d[s.history7d.length - 1].recordingCameras : null;
  // Only REAL, summary-derived KPIs here. SLA%/storage%/MTTR were hardcoded
  // (no source in the summary) and have been removed rather than fabricated —
  // SLA compliance lives on the Provider/Executive dashboards from real tickets.
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
      <KpiStat label={t('widgets.kpi.systemScore')} value={score == null ? '—' : score} unit={score == null ? undefined : '%'} delta={score == null ? undefined : s?.delta24h?.systemScore} tone={score == null ? 'info' : score >= 90 ? 'ok' : score >= 70 ? 'warning' : 'critical'} />
      <KpiStat label={t('widgets.kpi.totalDevices')} value={s?.totalDevices ?? 0} tone="info" />
      <KpiStat label={t('widgets.kpi.online')} value={s?.online ?? 0} delta={s?.delta24h?.online} tone="ok" />
      <KpiStat label={t('widgets.kpi.warning')} value={s?.warnings ?? 0} tone={s?.warnings ? 'warning' : 'ok'} />
      <KpiStat label={t('widgets.kpi.offlineCritical')} value={s?.offline ?? 0} tone={s?.offline ? 'critical' : 'ok'} />
      <KpiStat label={t('widgets.kpi.openIssues')} value={openIssues} tone={openIssues > 0 ? 'warning' : 'ok'} />
      <KpiStat label={t('widgets.kpi.recordingCameras')} value={recCams == null ? '—' : `${recCams}/${s?.totalDevices ?? 0}`} tone="info" />
    </div>
  );
}

function SystemGaugeWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  const score = s?.systemScore;
  // No devices → no score to gauge. Show a neutral "no devices" state instead of
  // a misleading 0% red ring.
  if (score == null) {
    return (
      <Card title={t('widgets.systemHealth.title')}>
        <div className="grid h-48 place-items-center text-center">
          <div className="text-sm text-muted">{t('widgets.systemHealth.noDevices')}</div>
        </div>
      </Card>
    );
  }
  const scoreColor = score >= 90 ? OK : score >= 70 ? WARN : CRIT;
  return (
    <Card title={t('widgets.systemHealth.title')}>
      <div className="relative h-48">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ name: 'score', value: score, fill: scoreColor }]} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" background={{ fill: 'rgba(255,255,255,0.06)' }} cornerRadius={12} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="tk-num text-4xl font-semibold" style={{ color: scoreColor }}>{score}<span className="text-lg">%</span></div>
            <div className="text-xs text-muted">{t('widgets.systemHealth.devices', { count: s?.totalDevices ?? 0 })}</div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ScoreTrendWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  // Time-range knob: keep only the last N hours of the hourly history.
  const hours = Number((config?.hours as number | undefined) ?? 24);
  const all = s?.history7d ?? [];
  const data = hours > 0 ? all.slice(-hours) : all;
  return (
    <Card title={t('widgets.scoreTrend.title')}>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="g-score" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.5} />
                <stop offset="100%" stopColor={PRIMARY} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="capturedAt" tickFormatter={(v) => new Date(v).getHours() + 'h'} tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
            <YAxis domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" width={28} />
            <Tooltip contentStyle={tooltip} labelFormatter={(v) => new Date(v).toLocaleTimeString()} />
            <Area type="monotone" dataKey="systemScore" stroke={PRIMARY} strokeWidth={2} fill="url(#g-score)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function DeviceStatusDonutWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  return (
    <Card title={t('widgets.deviceStatus.title')}>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[
                { name: t('widgets.deviceStatus.online'), value: s?.online ?? 0, fill: OK },
                { name: t('widgets.deviceStatus.warning'), value: s?.warnings ?? 0, fill: WARN },
                { name: t('widgets.deviceStatus.offline'), value: s?.offline ?? 0, fill: CRIT },
              ]}
              dataKey="value" nameKey="name" innerRadius="60%" outerRadius="85%" paddingAngle={3}
            >
              {[OK, WARN, CRIT].map((c) => <Cell key={c} fill={c} stroke="none" />)}
            </Pie>
            <Tooltip contentStyle={tooltip} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-center gap-4 text-xs text-muted">
        <span className="text-ok">● {t('widgets.deviceStatus.online')} {s?.online ?? 0}</span>
        <span className="text-warning">● {t('widgets.deviceStatus.warn')} {s?.warnings ?? 0}</span>
        <span className="text-critical">● {t('widgets.deviceStatus.offline')} {s?.offline ?? 0}</span>
      </div>
    </Card>
  );
}

function SiteHealthBarWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  const topN = Number((config?.topN as number | undefined) ?? 0);
  // Worst-first (lowest score). Cap to N when set.
  const sites = (s?.sites ?? []).slice().sort((a, b) => a.score - b.score);
  const data = topN > 0 ? sites.slice(0, topN) : sites;
  return (
    <Card title={t('widgets.siteHealth.title')}>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
            <YAxis type="category" dataKey="siteName" tick={{ fill: '#e2e8f0', fontSize: 12 }} width={110} stroke="rgba(255,255,255,0.1)" />
            <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={tooltip} />
            <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={16}>
              {data.map((site) => (
                <Cell key={site.siteId} fill={site.score >= 90 ? OK : site.score >= 70 ? WARN : CRIT} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function RollupsWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  return (
    <Card title={t('widgets.rollups.title')}>
      <div className="space-y-3">
        <RollupRow label={t('widgets.rollups.globalPlatform')} score={s?.globalScore ?? s?.systemScore ?? null} />
        {(s?.customerScores ?? []).map((cs) => <RollupRow key={cs.id} label={t('widgets.rollups.customer', { name: cs.name })} score={cs.score} sub />)}
        {(s?.regionalScores ?? []).map((rs) => <RollupRow key={rs.id} label={t('widgets.rollups.region', { name: rs.name })} score={rs.score} sub />)}
      </div>
    </Card>
  );
}

function ScoreBreakdownWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  const breakdown = s?.breakdown ?? [];
  return (
    <Card title={t('widgets.breakdown.title')}>
      <div className="space-y-2">
        {breakdown.length === 0 && <div className="text-sm text-muted">{t('widgets.breakdown.empty')}</div>}
        {breakdown.map((b) => {
          const max = breakdown[0]?.pointsLost || 1;
          return (
            <div key={b.event}>
              <div className="flex items-center justify-between text-xs">
                <span>{b.label} <span className="text-muted">×{b.count}</span></span>
                <span className="tk-num text-critical">−{b.pointsLost}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                <div className="h-full rounded-full bg-critical/70" style={{ width: `${(b.pointsLost / max) * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function InfraOverviewWidget() {
  const { t } = useTranslation('components');
  const { data: s } = useSummary();
  // REAL device mix from the live summary (per-driver counts), not fabricated.
  const mix = s?.driverMix ?? [];
  return (
    <Card title={t('widgets.infra.title')}>
      {mix.length === 0 ? (
        <div className="grid h-full place-items-center text-sm text-muted">{t('widgets.infra.empty')}</div>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {mix.map((m) => (
              <tr key={m.driverSlug} className="border-b border-white/5 last:border-0">
                <td className="py-2 text-muted">{m.driverSlug}</td>
                <td className="tk-num py-2 text-right font-medium">{m.count.toLocaleString()}</td>
              </tr>
            ))}
            <tr className="border-t border-white/10">
              <td className="py-2 font-medium">{t('widgets.infra.totalDevices')}</td>
              <td className="tk-num py-2 text-right font-semibold">{(s?.totalDevices ?? 0).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Card>
  );
}

// Inner card shell (widgets render their own header; the grid tile is frameless).
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{title}</div>
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

function RollupRow({ label, score, sub }: { label: string; score: number | null; sub?: boolean }) {
  // null score (no devices) → em-dash + empty bar, not a fake 0%.
  const color = score == null ? 'var(--muted, #94a3b8)' : score >= 90 ? OK : score >= 70 ? WARN : CRIT;
  return (
    <div className={sub ? 'pl-2' : ''}>
      <div className="flex items-center justify-between text-xs">
        <span className={sub ? 'text-muted' : 'font-medium'}>{label}</span>
        <span className="tk-num font-semibold" style={{ color }}>{score == null ? '—' : `${score}%`}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
        <div className="h-full rounded-full" style={{ width: `${score ?? 0}%`, background: color }} />
      </div>
    </div>
  );
}

// ── PROVIDER widgets (MSP / multi-customer) — read inventoryApi.provider() ───
function useProvider() {
  return useQuery({ queryKey: ['provider'], queryFn: inventoryApi.provider, refetchInterval: 30000, retry: false });
}
function ProviderKpisWidget() {
  const { t } = useTranslation('components');
  const { data } = useProvider(); const k = data?.kpis;
  return (
    <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-4">
      <KpiStat label={t('widgets.providerKpis.managedCustomers')} value={k?.managedCustomers ?? 0} tone="info" />
      <KpiStat label={t('widgets.providerKpis.managedDevices')} value={(k?.managedDevices ?? 0).toLocaleString()} tone="info" />
      <KpiStat label={t('widgets.providerKpis.activeIncidents')} value={k?.activeIncidents ?? 0} tone={(k?.activeIncidents ?? 0) > 0 ? 'warning' : 'ok'} />
      <KpiStat label={t('widgets.providerKpis.slaPerformance')} value={k?.slaPerformance ?? 0} unit="%" tone={(k?.slaPerformance ?? 0) >= 95 ? 'ok' : (k?.slaPerformance ?? 0) >= 90 ? 'warning' : 'critical'} />
    </div>
  );
}
function CustomerRankingWidget() {
  const { t } = useTranslation('components');
  const { data } = useProvider();
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.customerRanking.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {(data?.customerRanking ?? []).map((c, i) => (
          <li key={`${c.customer}-${i}`} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{c.customer} <span className="text-xs text-muted">· {t('widgets.customerRanking.devices', { count: c.devices })}</span></span>
            <span className={`tk-num font-medium ${c.score >= 90 ? 'text-ok' : c.score >= 70 ? 'text-warning' : 'text-critical'}`}>{c.score}%</span>
          </li>
        ))}
        {(data?.customerRanking ?? []).length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.customerRanking.empty')}</li>}
      </ul>
    </div>
  );
}
function TechWorkloadWidget() {
  const { t: tr } = useTranslation('components');
  const { data } = useProvider();
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{tr('widgets.techWorkload.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {(data?.technicianWorkload ?? []).map((t, i) => (
          <li key={`${t.tech}-${i}`} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{t.tech}</span>
            <span className="tk-num text-xs text-muted">{tr('widgets.techWorkload.stats', { open: t.open, resolved: t.resolved7d })}</span>
          </li>
        ))}
        {(data?.technicianWorkload ?? []).length === 0 && <li className="py-4 text-center text-xs text-muted">{tr('widgets.techWorkload.empty')}</li>}
      </ul>
    </div>
  );
}
function ProviderTrendWidget() {
  const { t } = useTranslation('components');
  const { data } = useProvider();
  const series = (data?.kpis.monthlyHealthTrend ?? []).map((score, i) => ({ t: `M${i + 1}`, score }));
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.providerTrend.title')}</div>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 5, right: 8, bottom: 0, left: -20 }}>
            <defs><linearGradient id="g-prov" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={PRIMARY} stopOpacity={0.4} /><stop offset="100%" stopColor={PRIMARY} stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#94a3b8' }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={tooltip} />
            <Area type="monotone" dataKey="score" name={t('widgets.providerTrend.seriesName')} stroke={PRIMARY} strokeWidth={2} fill="url(#g-prov)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── EXECUTIVE widgets — read inventoryApi.executive() ────────────────────────
function useExec() {
  return useQuery({ queryKey: ['executive'], queryFn: inventoryApi.executive, refetchInterval: 30000, retry: false });
}
function ExecKpisWidget() {
  const { t } = useTranslation('components');
  const { data: d } = useExec();
  return (
    <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-4">
      <KpiStat label={t('widgets.execKpis.overallHealth')} value={d?.overallHealth ?? 0} unit="%" tone={(d?.overallHealth ?? 0) >= 95 ? 'ok' : (d?.overallHealth ?? 0) >= 85 ? 'warning' : 'critical'} />
      <KpiStat label={t('widgets.execKpis.cameraAvailability')} value={d?.cameraAvailability ?? 0} unit="%" tone={(d?.cameraAvailability ?? 0) >= 95 ? 'ok' : 'warning'} />
      <KpiStat label={t('widgets.execKpis.recordingHealth')} value={d?.recordingHealth ?? 0} unit="%" tone={(d?.recordingHealth ?? 0) >= 95 ? 'ok' : 'warning'} />
      <KpiStat label={t('widgets.execKpis.slaCompliance')} value={d?.slaCompliance ?? 0} unit="%" tone={(d?.slaCompliance ?? 0) >= 95 ? 'ok' : 'warning'} />
    </div>
  );
}
function ExecTrendWidget() {
  const { t } = useTranslation('components');
  const { data } = useExec();
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.execTrend.title')}</div>
      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data?.trend ?? []} margin={{ top: 5, right: 8, bottom: 0, left: -20 }}>
            <defs><linearGradient id="g-exec" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={OK} stopOpacity={0.4} /><stop offset="100%" stopColor={OK} stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#94a3b8' }} /><YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={tooltip} />
            <Area type="monotone" dataKey="score" name={t('widgets.execTrend.seriesName')} stroke={OK} strokeWidth={2} fill="url(#g-exec)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function ExecReportsWidget() {
  const { t } = useTranslation('components');
  const { data } = useExec();
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.execReports.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {(data?.reports ?? []).map((r, i) => (
          <li key={i} className="flex items-center justify-between border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{r.title}</span>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-muted ring-1 ring-white/10">{r.type}</span>
          </li>
        ))}
        {(data?.reports ?? []).length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.execReports.empty')}</li>}
      </ul>
    </div>
  );
}

// ── TICKETING / SLA / SYSTEM / VIDEO widgets ─────────────────────────────────
// All read real, DB-backed endpoints (itsm-routes, health, video-assurance,
// edges). Shared query keys → one fetch feeds repeated widgets. Empty arrays
// render a clean empty-state, never crash on undefined.

function useServiceTickets() {
  return useQuery({ queryKey: ['service-tickets'], queryFn: inventoryApi.serviceTickets, refetchInterval: 30000, retry: false });
}
function useSla() {
  return useQuery({ queryKey: ['sla-tracking'], queryFn: inventoryApi.slaTracking, refetchInterval: 30000, retry: false });
}
function useEscalations() {
  return useQuery({ queryKey: ['escalations'], queryFn: inventoryApi.escalations, refetchInterval: 30000, retry: false });
}
function useVendorPerf() {
  return useQuery({ queryKey: ['vendor-performance'], queryFn: inventoryApi.vendorPerformance, refetchInterval: 60000, retry: false });
}
function useEdges() {
  return useQuery({ queryKey: ['edges'], queryFn: edgesApi.list, refetchInterval: 15000, retry: false });
}
function useOpenIncidents() {
  return useQuery({ queryKey: ['incidents', 'open'], queryFn: () => healthApi.incidents({ status: 'open' }), refetchInterval: 15000, retry: false });
}
function useAutomation() {
  return useQuery({ queryKey: ['automation-rules'], queryFn: inventoryApi.automationRules, refetchInterval: 60000, retry: false });
}
function useVa() {
  return useQuery({ queryKey: ['va-summary'], queryFn: () => videoAssuranceApi.summary(), refetchInterval: 30000, retry: false });
}

const isOpenStatus = (s: string) => !/^(resolved|closed|done)$/i.test(s);

/** Compact relative time ("3m", "2h", "5d") — locale-agnostic, no deps. */
function ago(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

type Tone = 'critical' | 'warning' | 'info';
const PRIORITY_TONE = (p: string): Tone =>
  /p?1|critical/i.test(p) ? 'critical' : /p?2|high/i.test(p) ? 'warning' : 'info';
// Static class map — Tailwind can't see dynamically-built `text-${tone}` names,
// so map to literal classes it will generate.
const TONE_TEXT: Record<Tone, string> = { critical: 'text-critical', warning: 'text-warning', info: 'text-muted' };

// ── Ticketing / SLA ──────────────────────────────────────────────────────────
function TicketKpisWidget() {
  const { t } = useTranslation('components');
  const { data } = useServiceTickets();
  const tickets = data ?? [];
  const open = tickets.filter((x) => isOpenStatus(x.status)).length;
  const p1 = tickets.filter((x) => /p?1|critical/i.test(x.priority)).length;
  const breached = tickets.filter((x) => x.slaBreached).length;
  const escalated = tickets.filter((x) => x.escalationLevel > 0).length;
  const resolved = tickets.filter((x) => !isOpenStatus(x.status)).length;
  return (
    <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <KpiStat label={t('widgets.ticketKpis.open')} value={open} tone={open > 0 ? 'warning' : 'ok'} />
      <KpiStat label={t('widgets.ticketKpis.p1')} value={p1} tone={p1 > 0 ? 'critical' : 'ok'} />
      <KpiStat label={t('widgets.ticketKpis.breached')} value={breached} tone={breached > 0 ? 'critical' : 'ok'} />
      <KpiStat label={t('widgets.ticketKpis.escalated')} value={escalated} tone={escalated > 0 ? 'warning' : 'ok'} />
      <KpiStat label={t('widgets.ticketKpis.resolved')} value={resolved} tone="info" />
    </div>
  );
}

function TicketStatusWidget() {
  const { t } = useTranslation('components');
  const { data } = useServiceTickets();
  const counts = new Map<string, number>();
  for (const x of data ?? []) counts.set(x.status, (counts.get(x.status) ?? 0) + 1);
  const palette = [PRIMARY, WARN, OK, CRIT, '#a855f7', '#06b6d4'];
  const slice = [...counts.entries()].map(([name, value], i) => ({ name, value, fill: palette[i % palette.length] }));
  return (
    <Card title={t('widgets.ticketStatus.title')}>
      {slice.length === 0 ? (
        <div className="grid h-full place-items-center text-sm text-muted">{t('widgets.ticketStatus.empty')}</div>
      ) : (
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={slice} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="85%" paddingAngle={3}>
                {slice.map((s) => <Cell key={s.name} fill={s.fill} stroke="none" />)}
              </Pie>
              <Tooltip contentStyle={tooltip} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function SlaBreachesWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data } = useSla();
  const limit = Number((config?.limit as number | undefined) ?? 0);
  const breachedOnly = !!(config?.breachedOnly as boolean | undefined);
  const past = (target: number | null, actual: number | null) => actual != null && target != null && actual > target;
  let atRisk = (data ?? []).filter((r) => r.breached || (!breachedOnly && (past(r.responseTarget, r.responseActual) || past(r.resolutionTarget, r.resolutionActual))));
  if (limit > 0) atRisk = atRisk.slice(0, limit);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.slaBreaches.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {atRisk.map((r) => (
          <li key={r.ticketId} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{r.title} <span className="text-xs text-muted">· {r.customer}</span></span>
            <span className={`shrink-0 tk-num text-xs ${r.breached ? 'text-critical' : 'text-warning'}`}>
              {r.breached ? t('widgets.slaBreaches.breached') : t('widgets.slaBreaches.atRisk')}{r.escalationLevel > 0 ? ` · L${r.escalationLevel}` : ''}
            </span>
          </li>
        ))}
        {atRisk.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.slaBreaches.empty')}</li>}
      </ul>
    </div>
  );
}

function EscalationsWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data } = useEscalations();
  const limit = Number((config?.limit as number | undefined) ?? 0);
  const active = limit > 0 ? (data?.active ?? []).slice(0, limit) : (data?.active ?? []);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.escalations.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {active.map((e) => (
          <li key={e.ticketId} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{e.title} <span className="text-xs text-muted">· {e.site}</span></span>
            <span className="shrink-0 tk-num text-xs text-warning">L{e.level} · {ago(e.since)}</span>
          </li>
        ))}
        {active.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.escalations.empty')}</li>}
      </ul>
    </div>
  );
}

function VendorPerfWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data } = useVendorPerf();
  const limit = Number((config?.limit as number | undefined) ?? 0);
  const sortByVolume = !!(config?.sortByVolume as boolean | undefined);
  let rows = (data ?? []).slice();
  if (sortByVolume) rows.sort((a, b) => b.ticketVolume - a.ticketVolume);
  if (limit > 0) rows = rows.slice(0, limit);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.vendorPerf.title')}</div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="grid h-full place-items-center text-sm text-muted">{t('widgets.vendorPerf.empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted">
              <tr className="border-b border-white/10">
                <th className="py-1.5 text-start font-medium">{t('widgets.vendorPerf.vendor')}</th>
                <th className="py-1.5 text-end font-medium">{t('widgets.vendorPerf.sla')}</th>
                <th className="py-1.5 text-end font-medium">{t('widgets.vendorPerf.avgResolution')}</th>
                <th className="py-1.5 text-end font-medium">{t('widgets.vendorPerf.volume')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.vendor} className="border-b border-white/5 last:border-0">
                  <td className="py-1.5 truncate">{v.vendor}</td>
                  <td className={`tk-num py-1.5 text-end ${v.slaCompliance >= 95 ? 'text-ok' : v.slaCompliance >= 90 ? 'text-warning' : 'text-critical'}`}>{v.slaCompliance}%</td>
                  <td className="tk-num py-1.5 text-end text-muted">{v.avgResolutionMins}m</td>
                  <td className="tk-num py-1.5 text-end text-muted">{v.ticketVolume}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── System overview ──────────────────────────────────────────────────────────
function EdgeFleetWidget() {
  const { t } = useTranslation('components');
  const { data } = useEdges();
  const edges = data ?? [];
  const online = edges.filter((e) => e.connected).length;
  const offline = edges.length - online;
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.edgeFleet.title')}</div>
      <div className="mb-2 grid grid-cols-2 gap-3">
        <KpiStat label={t('widgets.edgeFleet.online')} value={online} tone="ok" />
        <KpiStat label={t('widgets.edgeFleet.offline')} value={offline} tone={offline > 0 ? 'critical' : 'ok'} />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto text-sm">
        {edges.map((e) => (
          <li key={e.edgeId} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{e.name || e.edgeId}</span>
            <span className={`shrink-0 text-xs ${e.connected ? 'text-ok' : 'text-critical'}`}>● {e.connected ? t('widgets.edgeFleet.statusOnline') : t('widgets.edgeFleet.statusOffline')}</span>
          </li>
        ))}
        {edges.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.edgeFleet.empty')}</li>}
      </ul>
    </div>
  );
}

function ActiveIncidentsWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data } = useOpenIncidents();
  const limit = Number((config?.limit as number | undefined) ?? 0);
  const minPriority = (config?.minPriority as string | undefined) ?? 'all';
  const rank = (p: string) => (/p?1|critical/i.test(p) ? 3 : /p?2|high/i.test(p) ? 2 : 1);
  const floor = minPriority === 'critical' ? 3 : minPriority === 'high' ? 2 : 0;
  let incidents = (data ?? []).slice()
    .filter((i) => rank(i.priority) >= floor)
    .sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime());
  if (limit > 0) incidents = incidents.slice(0, limit);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.activeIncidents.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {incidents.map((i) => (
          <li key={i.id} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{i.title}{i.deviceName ? <span className="text-xs text-muted"> · {i.deviceName}</span> : null}</span>
            <span className={`shrink-0 tk-num text-xs ${TONE_TEXT[PRIORITY_TONE(i.priority)]}`}>{i.priority} · {ago(i.reportedAt)}</span>
          </li>
        ))}
        {incidents.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.activeIncidents.empty')}</li>}
      </ul>
    </div>
  );
}

function AutomationActivityWidget() {
  const { t } = useTranslation('components');
  const { data } = useAutomation();
  const rules = data ?? [];
  const enabled = rules.filter((r) => r.enabled).length;
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.automation.title')}</div>
      <div className="mb-2 grid grid-cols-2 gap-3">
        <KpiStat label={t('widgets.automation.total')} value={rules.length} tone="info" />
        <KpiStat label={t('widgets.automation.enabled')} value={enabled} tone={enabled > 0 ? 'ok' : 'info'} />
      </div>
      <ul className="min-h-0 flex-1 overflow-y-auto text-sm">
        {rules.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 border-b border-white/5 py-1.5 last:border-0">
            <span className="truncate">{r.name}</span>
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ring-1 ${r.enabled ? 'text-ok ring-ok/30' : 'text-muted ring-white/10'}`}>
              {r.enabled ? t('widgets.automation.on') : t('widgets.automation.off')}
            </span>
          </li>
        ))}
        {rules.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.automation.empty')}</li>}
      </ul>
    </div>
  );
}

// ── Video assurance ──────────────────────────────────────────────────────────
function VaScoreWidget() {
  const { t } = useTranslation('components');
  const { data } = useVa();
  const score = data?.globalScore ?? 0;
  const c = score >= 90 ? OK : score >= 70 ? WARN : CRIT;
  const dist = data?.distribution ?? { healthy: 0, warning: 0, critical: 0 };
  return (
    <Card title={t('widgets.vaScore.title')}>
      <div className="relative h-36">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart innerRadius="72%" outerRadius="100%" data={[{ name: 'va', value: score, fill: c }]} startAngle={90} endAngle={-270}>
            <RadialBar dataKey="value" background={{ fill: 'rgba(255,255,255,0.06)' }} cornerRadius={12} />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="tk-num text-3xl font-semibold" style={{ color: c }}>{score}<span className="text-base">%</span></div>
        </div>
      </div>
      <div className="flex justify-center gap-4 text-xs text-muted">
        <span className="text-ok">● {dist.healthy}</span>
        <span className="text-warning">● {dist.warning}</span>
        <span className="text-critical">● {dist.critical}</span>
      </div>
    </Card>
  );
}

function VaChecksWidget() {
  const { t } = useTranslation('components');
  const { data } = useVa();
  const cs = data?.checkScores;
  const checks: Array<[string, number]> = cs ? [
    ['heartbeat', cs.heartbeat], ['recording', cs.recording], ['retention', cs.retention],
    ['timeSync', cs.timeSync], ['imageQuality', cs.imageQuality], ['blur', cs.blur],
  ] : [];
  return (
    <Card title={t('widgets.vaChecks.title')}>
      {checks.length === 0 ? (
        <div className="grid h-full place-items-center text-sm text-muted">{t('widgets.vaChecks.empty')}</div>
      ) : (
        <div className="space-y-2">
          {checks.map(([key, val]) => {
            const color = val >= 90 ? OK : val >= 70 ? WARN : CRIT;
            return (
              <div key={key}>
                <div className="flex items-center justify-between text-xs">
                  <span>{t(`widgets.vaChecks.${key}`)}</span>
                  <span className="tk-num" style={{ color }}>{val}%</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <div className="h-full rounded-full" style={{ width: `${val}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function VaSitesWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data } = useVa();
  const topN = Number((config?.topN as number | undefined) ?? 0);
  const heat = (data?.siteHeatmap ?? []).slice().sort((a, b) => a.score - b.score);
  const sites = topN > 0 ? heat.slice(0, topN) : heat;
  return (
    <Card title={t('widgets.vaSites.title')}>
      {sites.length === 0 ? (
        <div className="grid h-full place-items-center text-sm text-muted">{t('widgets.vaSites.empty')}</div>
      ) : (
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sites} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} stroke="rgba(255,255,255,0.1)" />
              <YAxis type="category" dataKey="siteName" tick={{ fill: '#e2e8f0', fontSize: 12 }} width={110} stroke="rgba(255,255,255,0.1)" />
              <Tooltip cursor={{ fill: 'rgba(255,255,255,0.04)' }} contentStyle={tooltip} />
              <Bar dataKey="score" radius={[0, 6, 6, 0]} barSize={16}>
                {sites.map((s) => <Cell key={s.siteId} fill={s.score >= 90 ? OK : s.score >= 70 ? WARN : CRIT} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ── Recent activity ──────────────────────────────────────────────────────────
// Honest "what's happening" feed merged from the freshest signals already
// available (new tickets + open incidents) — no new backend endpoint. A full
// per-actor audit feed would need a tenant-wide activity endpoint (follow-up).
function RecentActivityWidget({ config }: { config?: WidgetConfig }) {
  const { t } = useTranslation('components');
  const { data: tickets } = useServiceTickets();
  const { data: incidents } = useOpenIncidents();
  const limit = Number((config?.limit as number | undefined) ?? 30);
  const kinds = (config?.kinds as string | undefined) ?? 'both'; // both | tickets | incidents
  type Row = { id: string; kind: 'ticket' | 'incident'; label: string; ts: string; tone: 'info' | 'warning' | 'critical' };
  const rows: Row[] = [
    ...(kinds === 'incidents' ? [] : (tickets ?? []).map((x) => ({
      id: `t-${x.id}`, kind: 'ticket' as const, label: x.title, ts: x.createdAt, tone: PRIORITY_TONE(x.priority),
    }))),
    ...(kinds === 'tickets' ? [] : (incidents ?? []).map((x) => ({
      id: `i-${x.id}`, kind: 'incident' as const, label: x.title, ts: x.reportedAt, tone: PRIORITY_TONE(x.priority),
    }))),
  ].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime()).slice(0, limit > 0 ? limit : 30);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 text-sm font-medium">{t('widgets.recentActivity.title')}</div>
      <ul className="flex-1 overflow-y-auto text-sm">
        {rows.map((r) => {
          const Icon = r.kind === 'ticket' ? Ticket : Bell;
          return (
            <li key={r.id} className="flex items-center gap-2 border-b border-white/5 py-1.5 last:border-0">
              <Icon size={13} className={`shrink-0 ${TONE_TEXT[r.tone]}`} />
              <span className="min-w-0 flex-1 truncate">{r.label}</span>
              <span className="shrink-0 tk-num text-xs text-muted">{ago(r.ts)}</span>
            </li>
          );
        })}
        {rows.length === 0 && <li className="py-4 text-center text-xs text-muted">{t('widgets.recentActivity.empty')}</li>}
      </ul>
    </div>
  );
}

/** A user-tunable field for a widget (Part B). Declarative so WidgetGrid can
 *  render the settings form generically. `label` is an i18n key in `components`. */
export type WidgetConfigField =
  | { key: string; label: string; type: 'select'; options: Array<{ value: string; label: string }>; default: string }
  | { key: string; label: string; type: 'number'; min?: number; max?: number; step?: number; default: number }
  | { key: string; label: string; type: 'toggle'; default: boolean }
  | { key: string; label: string; type: 'multiselect'; options: Array<{ value: string; label: string }>; default: string[] };

export type WidgetConfig = Record<string, unknown>;

/** Resolve a widget's effective config = its schema defaults merged with the
 *  user's saved overrides. Widgets call this so they never see `undefined`. */
export function withDefaults(schema: WidgetConfigField[] | undefined, config: WidgetConfig | undefined): WidgetConfig {
  const out: WidgetConfig = {};
  for (const f of schema ?? []) out[f.key] = f.default;
  return { ...out, ...(config ?? {}) };
}

export interface WidgetDef {
  id: string; name: string; icon: typeof Gauge;
  defaultSize: { w: number; h: number }; minSize: { w: number; h: number };
  render: (config?: WidgetConfig) => JSX.Element;
  /** Which dashboard views offer this widget. Omitted = all views. */
  dashboards?: string[];
  /** Declarative settings exposed via the ⚙ gear in edit mode. Omit = no gear. */
  configSchema?: WidgetConfigField[];
}

// The registry. Grid is 12 cols; h is in row units (rowHeight≈64px). Each widget
// is tagged with the dashboard view(s) that offer it (operations widgets read
// the health summary; provider/executive widgets read their own APIs).
export const WIDGETS: WidgetDef[] = [
  // Operations
  { id: 'kpis', name: 'KPI row', icon: LayoutDashboard, dashboards: ['operations'], defaultSize: { w: 12, h: 4 }, minSize: { w: 6, h: 3 }, render: () => <KpiRowWidget /> },
  { id: 'gauge', name: 'System health gauge', icon: Gauge, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <SystemGaugeWidget /> },
  { id: 'trend', name: 'Score trend (24h)', icon: LineChartIcon, dashboards: ['operations'], defaultSize: { w: 8, h: 4 }, minSize: { w: 4, h: 3 }, render: (c) => <ScoreTrendWidget config={c} />, configSchema: [
    { key: 'hours', label: 'widgets.cfg.timeRange', type: 'select', default: '24', options: [
      { value: '12', label: 'widgets.cfg.range12h' }, { value: '24', label: 'widgets.cfg.range24h' },
      { value: '48', label: 'widgets.cfg.range48h' }, { value: '168', label: 'widgets.cfg.range7d' },
    ] },
  ] },
  { id: 'donut', name: 'Device status', icon: PieIcon, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <DeviceStatusDonutWidget /> },
  { id: 'sites', name: 'Site health', icon: BarChart3, dashboards: ['operations'], defaultSize: { w: 8, h: 4 }, minSize: { w: 4, h: 3 }, render: (c) => <SiteHealthBarWidget config={c} />, configSchema: [
    { key: 'topN', label: 'widgets.cfg.worstN', type: 'number', min: 0, max: 50, step: 1, default: 0 },
  ] },
  { id: 'rollups', name: 'Health rollups', icon: Layers, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <RollupsWidget /> },
  { id: 'breakdown', name: "What's costing points", icon: ListTree, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <ScoreBreakdownWidget /> },
  { id: 'infra', name: 'Infrastructure overview', icon: Boxes, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <InfraOverviewWidget /> },
  // Ticketing / SLA (operations — opt-in via Add widget)
  { id: 'ticket-kpis', name: 'Ticket KPIs', icon: Ticket, dashboards: ['operations'], defaultSize: { w: 12, h: 4 }, minSize: { w: 6, h: 3 }, render: () => <TicketKpisWidget /> },
  { id: 'ticket-status', name: 'Tickets by status', icon: PieIcon, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <TicketStatusWidget /> },
  { id: 'sla-breaches', name: 'SLA breaches', icon: ShieldAlert, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: (c) => <SlaBreachesWidget config={c} />, configSchema: [
    { key: 'limit', label: 'widgets.cfg.rowLimit', type: 'number', min: 0, max: 50, step: 1, default: 0 },
    { key: 'breachedOnly', label: 'widgets.cfg.breachedOnly', type: 'toggle', default: false },
  ] },
  { id: 'escalations-active', name: 'Active escalations', icon: ArrowUpCircle, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: (c) => <EscalationsWidget config={c} />, configSchema: [
    { key: 'limit', label: 'widgets.cfg.rowLimit', type: 'number', min: 0, max: 50, step: 1, default: 0 },
  ] },
  { id: 'vendor-perf', name: 'Vendor performance', icon: Truck, dashboards: ['operations'], defaultSize: { w: 6, h: 4 }, minSize: { w: 4, h: 3 }, render: (c) => <VendorPerfWidget config={c} />, configSchema: [
    { key: 'limit', label: 'widgets.cfg.rowLimit', type: 'number', min: 0, max: 50, step: 1, default: 0 },
    { key: 'sortByVolume', label: 'widgets.cfg.sortByVolume', type: 'toggle', default: false },
  ] },
  // System overview
  { id: 'edge-fleet', name: 'Edge fleet', icon: Server, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: () => <EdgeFleetWidget /> },
  { id: 'active-incidents', name: 'Active incidents', icon: Bell, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: (c) => <ActiveIncidentsWidget config={c} />, configSchema: [
    { key: 'limit', label: 'widgets.cfg.rowLimit', type: 'number', min: 0, max: 50, step: 1, default: 0 },
    { key: 'minPriority', label: 'widgets.cfg.minPriority', type: 'select', default: 'all', options: [
      { value: 'all', label: 'widgets.cfg.priAll' }, { value: 'high', label: 'widgets.cfg.priHigh' }, { value: 'critical', label: 'widgets.cfg.priCritical' },
    ] },
  ] },
  { id: 'automation-activity', name: 'Automation activity', icon: Workflow, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: () => <AutomationActivityWidget /> },
  // Video assurance
  { id: 'va-score', name: 'Video assurance score', icon: Video, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <VaScoreWidget /> },
  { id: 'va-checks', name: 'Video assurance checks', icon: ListChecks, dashboards: ['operations'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <VaChecksWidget /> },
  { id: 'va-sites', name: 'Video assurance by site', icon: BarChart3, dashboards: ['operations'], defaultSize: { w: 8, h: 4 }, minSize: { w: 4, h: 3 }, render: (c) => <VaSitesWidget config={c} />, configSchema: [
    { key: 'topN', label: 'widgets.cfg.worstN', type: 'number', min: 0, max: 50, step: 1, default: 0 },
  ] },
  // Recent activity
  { id: 'recent-activity', name: 'Recent activity', icon: Clock, dashboards: ['operations'], defaultSize: { w: 4, h: 5 }, minSize: { w: 3, h: 3 }, render: (c) => <RecentActivityWidget config={c} />, configSchema: [
    { key: 'limit', label: 'widgets.cfg.rowLimit', type: 'number', min: 0, max: 100, step: 5, default: 30 },
    { key: 'kinds', label: 'widgets.cfg.show', type: 'select', default: 'both', options: [
      { value: 'both', label: 'widgets.cfg.kindsBoth' }, { value: 'tickets', label: 'widgets.cfg.kindsTickets' }, { value: 'incidents', label: 'widgets.cfg.kindsIncidents' },
    ] },
  ] },
  // Provider (MSP)
  { id: 'prov-kpis', name: 'Provider KPIs', icon: Briefcase, dashboards: ['provider'], defaultSize: { w: 12, h: 4 }, minSize: { w: 6, h: 3 }, render: () => <ProviderKpisWidget /> },
  { id: 'prov-trend', name: 'Monthly health trend', icon: LineChartIcon, dashboards: ['provider'], defaultSize: { w: 8, h: 4 }, minSize: { w: 4, h: 3 }, render: () => <ProviderTrendWidget /> },
  { id: 'prov-ranking', name: 'Customer ranking', icon: Users, dashboards: ['provider'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <CustomerRankingWidget /> },
  { id: 'prov-workload', name: 'Technician workload', icon: Users, dashboards: ['provider'], defaultSize: { w: 6, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <TechWorkloadWidget /> },
  // Executive
  { id: 'exec-kpis', name: 'Executive KPIs', icon: AlertTriangle, dashboards: ['executive'], defaultSize: { w: 12, h: 4 }, minSize: { w: 6, h: 3 }, render: () => <ExecKpisWidget /> },
  { id: 'exec-trend', name: 'Health trend', icon: Activity, dashboards: ['executive'], defaultSize: { w: 8, h: 4 }, minSize: { w: 4, h: 3 }, render: () => <ExecTrendWidget /> },
  { id: 'exec-reports', name: 'Executive reports', icon: FileBarChart, dashboards: ['executive'], defaultSize: { w: 4, h: 4 }, minSize: { w: 3, h: 3 }, render: () => <ExecReportsWidget /> },
];

export const WIDGET_BY_ID = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));

/** Widgets offered on a given dashboard view (default = all when untagged). */
export const widgetsForDashboard = (dashboard: string) =>
  WIDGETS.filter((w) => !w.dashboards || w.dashboards.includes(dashboard));

// Per-view default layouts (Add-widget exposes the rest in Edit mode).
export const DEFAULT_LAYOUTS: Record<string, Array<{ i: string; x: number; y: number; w: number; h: number }>> = {
  operations: [
    { i: 'kpis', x: 0, y: 0, w: 12, h: 4 },
    { i: 'gauge', x: 0, y: 4, w: 4, h: 4 },
    { i: 'trend', x: 4, y: 4, w: 8, h: 4 },
    { i: 'donut', x: 0, y: 8, w: 4, h: 4 },
    { i: 'sites', x: 4, y: 8, w: 8, h: 4 },
  ],
  provider: [
    { i: 'prov-kpis', x: 0, y: 0, w: 12, h: 4 },
    { i: 'prov-trend', x: 0, y: 4, w: 8, h: 4 },
    { i: 'prov-ranking', x: 8, y: 4, w: 4, h: 4 },
    { i: 'prov-workload', x: 0, y: 8, w: 6, h: 4 },
  ],
  executive: [
    { i: 'exec-kpis', x: 0, y: 0, w: 12, h: 4 },
    { i: 'exec-trend', x: 0, y: 4, w: 8, h: 4 },
    { i: 'exec-reports', x: 8, y: 4, w: 4, h: 4 },
  ],
};
// Back-compat: the operations default (used as the generic fallback).
export const DEFAULT_LAYOUT = DEFAULT_LAYOUTS.operations;
